import React, { useState, useEffect, useRef, useMemo } from 'react';
import Storages from 'storages';
import onMainMenuKeyPress from 'utils/common/checkKeyPres';
import EventBus from 'utils/eventBus';
import StorageKeys from 'enum/storages.enum';
import getIsRTLLanguage from 'utils/common/isRTL';
import { bottomNavigationItems, getEffectiveMenuList } from 'utils/menuList';
import { useNavigate } from 'react-router-dom';
import { deepClone } from 'utils/common/deepClone';
import { scrollIntoView } from 'utils/dom';
import Blue from './blue';
import Red from './red';
import Warwick from './warwick';
import { Landing } from 'enum/design';
import { trackPage } from './common/utils';
import { getCurrentMultiListPosition } from 'utils/common/navigationHelpers';
import { buildSourceRoutePath } from 'utils/hdmi/hdmiSettingsData';
import MenuId from 'enum/menu-id.enum';
import Route from 'enum/route.enum';
import CastingManager from 'castingManager/CastingManager';

function Main({ isActive = true }) {
  const KEY_DOWN = 'keydown';
  const isRTL = getIsRTLLanguage();
  const navigate = useNavigate();

  const [mainMenuList, setMainMenuList] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState({ listIndex: 0, itemIndex: 0 });
  const [customerData, setCustomerData] = useState({});
  const [landingPage, setLandingPage] = useState(
    Storages.MainStorage.getItem(StorageKeys.Keys.ACTIVETEMPLATE)
  );
  const [castingDevices, setCastingDevice] = useState(CastingManager.getDevices());
  const [menuRevision, setMenuRevision] = useState(0);
  const isWarwickLayout =
    landingPage === Landing.Warwick || landingPage === Landing.WarwickV1;

  const baseMenuList = useMemo(
    () => getEffectiveMenuList(isWarwickLayout),
    [isWarwickLayout, castingDevices, menuRevision]
  );

  const baseMenuListRef = useRef(baseMenuList);
  baseMenuListRef.current = baseMenuList;

  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const hdmiChangeSubscription = CastingManager.subscribe(setCastingDevice);
    return () => hdmiChangeSubscription.unsubscribe();
  }, [isActive]);

  const getCustomerData = () => {
    const data = Storages.MainStorage.getItem(StorageKeys.Keys.CUSTOMERDATA);
    setCustomerData(data);
  };

  const getData = () => {
    setLandingPage(Storages.MainStorage.getItem(StorageKeys.Keys.ACTIVETEMPLATE));
  };

  const refreshLandingData = () => {
    getData();
    setMenuRevision((revision) => revision + 1);
  };

  const applyFocus = (listIndex, itemIndex) => {
    setFocusedIndex({ listIndex, itemIndex });
    setTimeout(() => {
      scrollIntoView('.main-menu-button', itemIndex);
    }, 10);
  };

  function switchToCasting(def_index = 0) {
    const device = castingDevices[def_index];
    const castingUrl = buildSourceRoutePath(device.sourceType, device.number);
    const returnContext = { state: { returnTo: Route.route.LANDING } };
    navigate(castingUrl, returnContext);
  }

  const navigateToPage = (list, index) => {
    const page = list[index];
    if (page?.id === MenuId.CAST) return switchToCasting();
    navigateRef.current(page?.link);
    trackPage(page?.id);
  };

  const updateMenuList = (index, updateFocusIndex = false, shouldNavigate = false) => {
    const list = baseMenuListRef.current;
    const newMenuList = list.map((item, i) => {
      const copy = deepClone(item);
      copy.active = i === index;
      return copy;
    });
    setMainMenuList(newMenuList);

    if (updateFocusIndex) {
      applyFocus(0, index);
    }
    if (shouldNavigate) {
      navigateToPage(list, index);
    }
  };

  const updateMultiListMenuState = (listIndex, itemIndex, shouldEnter) => {
    if (listIndex === 0) {
      updateMenuList(itemIndex, true, shouldEnter);
    } else if (listIndex === 1) {
      applyFocus(listIndex, itemIndex);
      if (shouldEnter) {
        navigateToPage(bottomNavigationItems, itemIndex);
      }
    }
  };

  const onKeyPressEvent = useRef(async (event) => {
    const data = await onMainMenuKeyPress(event);
    if (data !== null) {
      updateMultiListMenuState(data.listIndex, data.itemIndex, data.enter);
    }
  });

  useEffect(() => {
    getData();
    getCustomerData();
    const focusState = getCurrentMultiListPosition();
    applyFocus(focusState.listIndex, focusState.itemIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFocusedIndex((prev) => {
      if (prev.listIndex !== 0) {
        return prev;
      }

      const maxIndex = Math.max(0, baseMenuList.length - 1);
      if (prev.itemIndex <= maxIndex) {
        return prev;
      }

      return { ...prev, itemIndex: maxIndex };
    });
  }, [baseMenuList]);

  useEffect(() => {
    const newMenuList = baseMenuList.map((item, i) => {
      const copy = deepClone(item);
      copy.active = focusedIndex.listIndex === 0 && i === focusedIndex.itemIndex;
      return copy;
    });
    setMainMenuList(newMenuList);
  }, [focusedIndex, baseMenuList]);

  // Only listen for remote keys while Landing is the visible page
  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const handler = onKeyPressEvent.current;
    const onRefreshSub = EventBus.subscribe('refresh', refreshLandingData);
    const onRefreshCustomerDataSub = EventBus.subscribe('refreshCustomerData', getCustomerData);

    window.addEventListener(KEY_DOWN, handler);

    // Soft restore focus when returning (no remount)
    const focusState = getCurrentMultiListPosition();
    applyFocus(focusState.listIndex, focusState.itemIndex);

    return () => {
      window.removeEventListener(KEY_DOWN, handler);
      onRefreshSub.unsubscribe();
      onRefreshCustomerDataSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const getLandingPage = () => {
    if (landingPage === Landing.Classic) return Red;
    if (landingPage === Landing.Warwick || landingPage === Landing.WarwickV1) return Warwick;
    return Blue;
  };

  const Component = getLandingPage();

  return (
    <Component
      isRTL={isRTL}
      customerData={customerData}
      mainMenuList={mainMenuList}
      updateMenuList={updateMenuList}
      onBottomNavClick={(index) => updateMultiListMenuState(1, index, true)}
      focusedIndex={focusedIndex.itemIndex}
      focusedListIndex={focusedIndex.listIndex}
    />
  );
}

export default Main;
